import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  Stack,
  DrawerBody,
  VStack,
  HStack,
  Text,
  Textarea,
  Spacer,
  DrawerFooter,
  Button,
  Spinner,
  Input,
} from "@chakra-ui/react";
import HappycoPropertySelector from "./create-work-order/happyco-property-selector";
import {
  BulletListIcon,
  CalendarOutlineIcon,
  HappycoMiniLogoIcon,
  InfoIcon,
  KeyOutlineIcon,
  LocationIcon,
  PersonCircleIcon,
  WorkOrderIcon,
} from "modules/icons";
import colors from "app/colors";
import { useMutation } from "@apollo/client";
import {
  HappycoCreateWorkOrderInput,
  HappycoCreateWorkOrderMutation,
  HappycoCreateWorkOrderMutationVariables,
  HappycoPropertyFragment,
  HappycoResidentFragment,
  HappycoUnitFragment,
  HappycoUserFragment,
  HappycoWorkCategoryFragment,
  MessageFileInput,
} from "gql/graphql";
import HAPPYCO_CREATE_WORK_ORDER_MUTATION from "./_networking/happyco-create-work-order";
import NewMessageUploadAttachmentList from "modules/messaging/_shared/new-message-upload-attachment-list";
import { HAPPYCO_THREAD_DRAFT_ID } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import HappycoUnitSelector from "./create-work-order/happyco-unit-selector";
import { SingleValue } from "chakra-react-select";
import HappycoWorkOrderCategorySelector from "./create-work-order/happyco-work-order-category-selector";
import { getMessageFileInput } from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";
import HappycoResidentSelector from "./create-work-order/happyco-resident-selector";
import moment from "moment";
import HappycoUserSelector from "./create-work-order/happyco-user-selector";
import usePlainText from "modules/hooks/use-plain-text";
import useDisplayToast from "modules/hooks/use-display-toast";
import Toast from "modules/toasts/toast";

const HappycoCreateWorkOrderForm = () => {
  const { closeDrawer } = useOverlay();
  const { displayToast } = useDisplayToast();
  const { draft, files, deleteDraft } = useMessageThreadDraft({
    draftId: HAPPYCO_THREAD_DRAFT_ID,
  });

  const [happycoCreateWorkOrderAPI, { loading }] = useMutation<
    HappycoCreateWorkOrderMutation,
    HappycoCreateWorkOrderMutationVariables
  >(HAPPYCO_CREATE_WORK_ORDER_MUTATION);

  const [propertyId, setPropertyId] = useState<string | undefined>(undefined);
  const [unitId, setUnitId] = useState<string | undefined>(undefined);
  const [residentId, setResidentId] = useState<string | undefined>(undefined);
  const [assigneeId, setAssigneeId] = useState<string | undefined>(undefined);
  const [workCategoryId, setWorkCategoryId] = useState<string | undefined>(
    undefined,
  );
  const [description, setDescription] = useState<string | undefined>();
  const [scheduleTime, setScheduleTime] = useState<string | undefined>();

  const { getPlainText } = usePlainText();

  useEffect(() => {
    setDescription((prev) => {
      if (prev) {
        return prev;
      }
      return getPlainText(draft?.text ?? "");
    });
  }, [draft?.text, getPlainText]);

  const locationId = unitId ?? propertyId;

  const happycoCreateWorkOrderInput = useMemo(
    (): HappycoCreateWorkOrderInput | undefined =>
      locationId
        ? {
            locationId,
            description,
            workCategoryId,
            residentId,
            scheduleTime,
            assigneeId,
            fileList: files?.map(
              (file): MessageFileInput => getMessageFileInput(file),
            ),
          }
        : undefined,
    [
      locationId,
      description,
      workCategoryId,
      residentId,
      scheduleTime,
      assigneeId,
      files,
    ],
  );

  const onCreateWorkOrderClicked = useCallback(async () => {
    if (!happycoCreateWorkOrderInput) return;
    await happycoCreateWorkOrderAPI({
      variables: {
        input: happycoCreateWorkOrderInput,
      },
    });
    displayToast(() => (
      <Toast
        message="New work order sent to HappyCo"
        leftIcon={<WorkOrderIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    deleteDraft();
    closeDrawer();
  }, [
    happycoCreateWorkOrderInput,
    happycoCreateWorkOrderAPI,
    displayToast,
    deleteDraft,
    closeDrawer,
  ]);

  const onPropertySelected = useCallback(
    (property?: SingleValue<HappycoPropertyFragment>) => {
      setPropertyId(property?.happycoId);
    },
    [],
  );

  const onUnitSelected = useCallback(
    (unit?: SingleValue<HappycoUnitFragment>) => {
      setUnitId(unit?.happycoId);
    },
    [],
  );

  const onResidentSelected = useCallback(
    (resident?: SingleValue<HappycoResidentFragment>) => {
      setResidentId(resident?.happycoId);
    },
    [],
  );

  const onUserSelected = useCallback(
    (user?: SingleValue<HappycoUserFragment>) => {
      setAssigneeId(user?.happycoId);
    },
    [],
  );

  const onWorkCategorySelected = useCallback(
    (workCategory?: SingleValue<HappycoWorkCategoryFragment>) => {
      setWorkCategoryId(workCategory?.happycoId);
    },
    [],
  );

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(e.target.value);
    },
    [],
  );

  const onScheduleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setScheduleTime(moment(e.target.value).toISOString());
    },
    [],
  );

  return (
    <>
      <DrawerBody px={3} pt={6}>
        <Stack w="full" h="full" spacing={6}>
          <VStack>
            <HStack w="full" spacing={1}>
              <LocationIcon boxSize={5} fill={colors.black} />
              <Text
                fontFamily="Outfit"
                fontWeight={600}
                fontSize={18}
                color={colors.blackSecondary}
              >
                Property
              </Text>
            </HStack>
            <HappycoPropertySelector onPropertySelected={onPropertySelected} />
          </VStack>
          <VStack>
            <HStack w="full" spacing={1}>
              <InfoIcon boxSize={5} fill={colors.black} />
              <Text
                fontFamily="Outfit"
                fontWeight={600}
                fontSize={18}
                color={colors.blackSecondary}
              >
                Details
              </Text>
              <Text
                fontFamily="Outfit"
                fontWeight={500}
                fontSize={14}
                color={colors.grayMain}
              >
                (Optional)
              </Text>
            </HStack>
            <HappycoUnitSelector
              propertyId={propertyId}
              onUnitSelected={onUnitSelected}
            />
            <HappycoWorkOrderCategorySelector
              propertyId={propertyId}
              onWorkCategorySelected={onWorkCategorySelected}
            />
          </VStack>
          <VStack>
            <HStack w="full" spacing={1}>
              <BulletListIcon boxSize={5} fill={colors.black} />
              <Text
                fontFamily="Outfit"
                fontWeight={600}
                fontSize={18}
                color={colors.blackSecondary}
              >
                Description
              </Text>
            </HStack>
            <VStack
              borderRadius={8}
              borderColor={colors.grayPrimary}
              borderWidth={1}
              w="full"
            >
              <Textarea
                placeholder="Enter description..."
                fontSize={16}
                color={colors.blackSecondary}
                fontWeight={500}
                border="none"
                w="full"
                outline="none"
                boxShadow="none"
                _hover={{
                  border: "none",
                }}
                _focus={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                }}
                _focusVisible={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                }}
                minH={110}
                resize="none"
                onChange={onDescriptionChange}
                value={description}
              />
              <NewMessageUploadAttachmentList
                draftId={HAPPYCO_THREAD_DRAFT_ID}
                files={files}
              />
            </VStack>
          </VStack>
          <VStack>
            <HStack w="full" spacing={1}>
              <KeyOutlineIcon boxSize={5} fill={colors.black} />
              <Text
                fontFamily="Outfit"
                fontWeight={600}
                fontSize={18}
                color={colors.blackSecondary}
              >
                Resident Info
              </Text>
              <Text
                fontFamily="Outfit"
                fontWeight={500}
                fontSize={14}
                color={colors.grayMain}
              >
                (Optional)
              </Text>
            </HStack>
            <HappycoResidentSelector
              propertyId={propertyId}
              unitId={unitId}
              onResidentSelected={onResidentSelected}
            />
          </VStack>
          <VStack>
            <HStack w="full" spacing={1}>
              <PersonCircleIcon boxSize={5} fill={colors.black} />
              <Text
                fontFamily="Outfit"
                fontWeight={600}
                fontSize={18}
                color={colors.blackSecondary}
              >
                Assigned To
              </Text>
              <Text
                fontFamily="Outfit"
                fontWeight={500}
                fontSize={14}
                color={colors.grayMain}
              >
                (Optional)
              </Text>
            </HStack>
            <HappycoUserSelector onUserSelected={onUserSelected} />
          </VStack>
          <VStack>
            <HStack w="full" spacing={1}>
              <CalendarOutlineIcon boxSize={5} fill={colors.black} />
              <Text
                fontFamily="Outfit"
                fontWeight={600}
                fontSize={18}
                color={colors.blackSecondary}
              >
                Scheduling
              </Text>
              <Text
                fontFamily="Outfit"
                fontWeight={500}
                fontSize={14}
                color={colors.grayMain}
              >
                (Optional)
              </Text>
            </HStack>
            <Input
              placeholder="Select Date"
              size="md"
              type="date"
              onChange={onScheduleTimeChange}
              value={
                scheduleTime ? moment(scheduleTime).format("YYYY-MM-DD") : ""
              }
            />
          </VStack>
        </Stack>
      </DrawerBody>
      <DrawerFooter h="96px" boxShadow="0px -4px 4px rgba(0, 0, 0, 0.05)">
        <HStack w="full">
          <Spacer />
          <Button
            variant="hover"
            bg={colors.darkBlue}
            color={colors.white}
            fontSize={11}
            fontWeight={700}
            onClick={onCreateWorkOrderClicked}
            isDisabled={!locationId}
            leftIcon={
              loading ? (
                <Spinner />
              ) : (
                <HappycoMiniLogoIcon fill={colors.white} w={4} h={4} />
              )
            }
            rounded="full"
            size="md"
          >
            Create Work Order
          </Button>
        </HStack>
      </DrawerFooter>
    </>
  );
};

export default memo(HappycoCreateWorkOrderForm);
